import React from "react";
import styled from "styled-components";
import { formatModelName } from "../config/providerModels";

// Shared styled components
export const ResponseTabsContainer = styled.div`
  display: flex;
  overflow-x: auto;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

export const ResponseTab = styled.button`
  background: ${({ $active }) => ($active ? "white" : "rgba(0, 0, 0, 0.05)")};
  border: none;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: ${({ $active }) => ($active ? "#000" : "#6c757d")};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  white-space: nowrap;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.5rem;

  &:hover:not(:disabled) {
    background: ${({ $active }) => ($active ? "white" : "rgba(0, 0, 0, 0.1)")};
    color: #000;
  }

  &:disabled {
    opacity: ${({ $active }) => ($active ? 1 : 0.5)};
    cursor: not-allowed;
  }

  &:first-child {
    margin-left: 0;
  }
`;

// Get tab label for a bot response (unified approach)
export const getAdminTabLabel = (botResponse, index = 0) => {
  // For re-ask responses, use the reaskTo field if available
  if (botResponse.reaskTo) {
    return botResponse.reaskTo;
  }

  // Use model information if available
  if (botResponse.model) {
    return formatModelName(botResponse.model);
  }

  // Use provider as fallback
  if (botResponse.provider) {
    return botResponse.provider;
  }

  // If we have multiple responses without model/provider info, number them
  if (index > 0) {
    return `Response ${index + 1}`;
  }

  // Final fallback
  return "Response";
};

// ResponseTabs component for AdminThread (simpler, display-only)
export const AdminResponseTabs = ({
  responses,
  activeTabId,
  onTabClick,
  groupId
}) => {
  // Only show tabs if multiple responses OR if we have model/provider info to display
  const shouldShowTabs =
    responses.length > 1 || responses.some((r) => r.model || r.provider);

  if (!shouldShowTabs) return null;

  return (
    <ResponseTabsContainer>
      {responses.map((response, index) => (
        <ResponseTab
          key={response.id}
          $active={
            activeTabId === response.id ||
            (activeTabId === undefined && index === 0)
          }
          onClick={() => onTabClick(groupId, response.id)}
        >
          {getAdminTabLabel(response, index)}
        </ResponseTab>
      ))}
    </ResponseTabsContainer>
  );
};

// ResponseTabs component for Messages (more complex, with re-ask functionality)
export const MessagesResponseTabs = ({
  group,
  activeTabId,
  onTabClick,
  getAllReaskOptions,
  selectedBot,
  isStreaming = false
}) => {
  // Only show tabs if multiple responses or re-ask options are available, and it's not a welcome message, and selectedBot has enableReAsk, and no message has disableReAsk
  const shouldShowTabs =
    selectedBot?.enableReAsk === true &&
    !group.botResponses.some((r) => r.disableReAsk === true) &&
    (group.botResponses.length > 1 || getAllReaskOptions().length > 0) &&
    !group.botResponses.some((r) => r.isWelcome) &&
    group.botResponses.some((r) => r.content && !r.isWelcome);

  if (!shouldShowTabs) return null;

  // Build constant set of tabs - never changes
  const originalResponse = group.botResponses.find((r) => r.isOriginalResponse);
  const originalModel = originalResponse?.model || selectedBot?.model;

  const tabs = [
    // Original response tab (always first)
    {
      id: originalResponse?.id || "original",
      label: (() => {
        // If response exists but doesn't have model info, merge with selectedBot's model
        const responseWithFallback = originalResponse
          ? {
            ...originalResponse,
            model: originalModel
          }
          : {
            isOriginalResponse: true,
            model: originalModel
          };
        return getAdminTabLabel(responseWithFallback, 0);
      })(),
      isPlaceholder: false,
      response: originalResponse
    },
    // Fixed set of re-ask options (filter out options that match the original model)
    ...getAllReaskOptions()
      .filter((option) => option.model !== originalModel)
      .map((option, index) => {
        // Find if we have a response for this model (match by model or reaskTo)
        const matchingResponse = group.botResponses.find((response) => {
          // Match by reaskTo or model
          if (
            response.reaskTo === option.name ||
            response.model === option.model
          ) {
            return true;
          }

          // Match by ID pattern - ensure exact match to avoid cross-matching
          if (response.isReaskResponse && !response.isOriginalResponse) {
            const responseId = String(response.id);
            const expectedPrefix = `reask-${option.id}-`;

            // Check if it starts with our prefix AND the next character after the prefix
            // is either the end of string or the group delimiter
            if (responseId.startsWith(expectedPrefix)) {
              const afterPrefix = responseId.substring(expectedPrefix.length);
              // Should start with "group_" to ensure we match the exact option
              return afterPrefix.startsWith("group_");
            }
          }

          return false;
        });

        return {
          id: matchingResponse?.id || `placeholder-${option.id}-${group.id}`,
          label: option.name,
          isPlaceholder: !matchingResponse,
          option: option,
          response: matchingResponse,
          originalResponseId: group.botResponses.find(
            (r) => r.isOriginalResponse
          )?.id
        };
      })
  ];

  return (
    <ResponseTabsContainer>
      {tabs.map((tab) => (
        <ResponseTab
          key={tab.id}
          $active={activeTabId === tab.id}
          onClick={() =>
            !isStreaming &&
            onTabClick(
              group.id,
              tab.id,
              tab.isPlaceholder,
              tab.option,
              tab.originalResponseId
            )
          }
          disabled={isStreaming}
        >
          {tab.label}
        </ResponseTab>
      ))}
    </ResponseTabsContainer>
  );
};
